import { useQueryClient } from '@tanstack/react-query'
import { useApiClient, type InferRequest } from '../../hooks/useApiClient'

// Import specialized hooks
import { useMatches } from './useMatches'
import { useMatchActions } from './useMatchActions'
import { useInvoiceActions } from './useInvoiceActions'
import { useMatchDetail } from './useMatchDetail'
import { useRecentActivity } from './useRecentActivity'
import { useSupplierRecDashboardTrends } from './useSupplierRecDashboardTrends'
import { useMatchTaxActions } from './useMatchTaxActions'

// Import types for proper typing
import type { MetadataResponse } from './types/api'
import { useMatchCacheUtils } from './cacheUtils'

// Re-export all types from the types directory
export * from './types'

// Types for optimistic updates
interface OptimisticUpdateContext {
  previousData: [unknown, unknown][]
}

interface MatchDetailRow {
  terminal?: {
    source_name: string
    internal_id?: string
    internal_name?: string
  }
  products?: Array<{
    source_name: string
    internal_id?: string
    internal_name?: string
  }>
}

interface MatchDetailData {
  document_detail_rows: MatchDetailRow[]
  [key: string]: unknown
}

export const useSupplierReconciliation = () => {
  const queryClient = useQueryClient()
  const $api = useApiClient()

  // Import specialized hooks
  const matchHooks = useMatches()
  const matchActionHooks = useMatchActions()
  const invoiceActionHooks = useInvoiceActions()
  const matchDetailHooks = useMatchDetail()
  const recentActivityHooks = useRecentActivity()
  const trendsHook = useSupplierRecDashboardTrends()
  const taxActionHooks = useMatchTaxActions()
  const cacheUtils = useMatchCacheUtils(queryClient)

  // Configuration-related hooks (kept in main hook for now)
  const useConfigOverview = () => {
    return $api.useQuery('post', '/supplier_rec/config/overview')
  }

  const useUpsertConfig = () => {
    return $api.useMutation('post', '/supplier_rec/config/upsert', {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/config/overview'],
        })
      },
    })
  }

  const useUpdateDefaultConfig = () => {
    return $api.useMutation('post', '/supplier_rec/config/default/update', {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/config/overview'],
        })
      },
    })
  }

  // Mapping-related hooks (kept in main hook for now)
  const useAddSupplierMapping = () => {
    return $api.useMutation('post', '/supplier_rec/mapping/supplier/add', {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/config/overview'],
        })
      },
    })
  }

  const useUpsertTerminalMapping = () => {
    return $api.useMutation('post', '/supplier_rec/mapping/terminal/upsert', {
      onMutate: async (request) => {
        // Cancel any outgoing queries to prevent race conditions
        await queryClient.cancelQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        // Snapshot previous data for rollback
        const previousData = queryClient.getQueriesData({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        // Extract mapping data from request
        const { supplier_id, source_name, terminal_ids } = request.body || {}

        // Optimistically update match detail cache
        if (terminal_ids && terminal_ids.length > 0 && source_name) {
          // Find the mapped terminal name from metadata
          const metadata = queryClient.getQueryData<MetadataResponse>(['metadata', 'supplier_config'])
          const locations = metadata?.locations as Array<{ id: string; name: string }> | undefined
          const mappedTerminal = locations?.find((loc) => loc.id === terminal_ids[0])

          if (mappedTerminal) {
            queryClient.setQueriesData(
              { queryKey: ['post', '/supplier_rec/match/detail'] },
              (oldData: MatchDetailData | undefined) => {
                if (!oldData || !oldData.document_detail_rows) return oldData

                const updatedRows = oldData.document_detail_rows.map((row) => {
                  if (row.terminal && row.terminal.source_name === source_name && !row.terminal.internal_id) {
                    return {
                      ...row,
                      terminal: {
                        ...row.terminal,
                        internal_id: terminal_ids[0],
                        internal_name: mappedTerminal?.name || '',
                      },
                    }
                  }
                  return row
                })

                return {
                  ...oldData,
                  document_detail_rows: updatedRows,
                }
              }
            )
          }
        }

        return { previousData }
      },
      onSuccess: () => {
        // Invalidate terminal mapping overview
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/mapping/terminal/overview'],
        })
        // Invalidate match detail queries to refresh with server data
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })
        // Invalidate metadata queries to refresh mapping options
        queryClient.invalidateQueries({
          queryKey: ['metadata'],
        })
      },
      onError: (error, request, context: OptimisticUpdateContext | undefined) => {
        // Rollback optimistic update on error
        if (context?.previousData && context.previousData.length > 0) {
          context.previousData.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey as string[], data)
          })
        }
      },
    })
  }

  const useUpsertProductMapping = () => {
    return $api.useMutation('post', '/supplier_rec/mapping/product/upsert', {
      onMutate: async (request) => {
        // Cancel any outgoing queries to prevent race conditions
        await queryClient.cancelQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        // Snapshot previous data for rollback
        const previousData = queryClient.getQueriesData({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        // Extract mapping data from request
        const { supplier_id, source_name, product_ids } = request.body || {}

        // Optimistically update match detail cache
        if (product_ids && product_ids.length > 0 && source_name) {
          // Find the mapped product name from metadata
          const metadata = queryClient.getQueryData<MetadataResponse>(['metadata', 'supplier_config'])
          let mappedProduct: { id: string; name: string } | null = null

          // Search through product hierarchy to find the product
          if (metadata?.product_hierarchy) {
            for (const group of Object.values(metadata.product_hierarchy)) {
              if (Array.isArray(group)) {
                mappedProduct = group.find((product) => product.id === product_ids[0]) || null
                if (mappedProduct) break
              }
            }
          }

          if (mappedProduct) {
            queryClient.setQueriesData(
              { queryKey: ['post', '/supplier_rec/match/detail'] },
              (oldData: MatchDetailData | undefined) => {
                if (!oldData || !oldData.document_detail_rows) return oldData

                const updatedRows = oldData.document_detail_rows.map((row) => {
                  if (row.products) {
                    const updatedProducts = row.products.map((product) => {
                      if (product.source_name === source_name && !product.internal_id) {
                        return {
                          ...product,
                          internal_id: product_ids[0],
                          internal_name: mappedProduct!.name,
                        }
                      }
                      return product
                    })
                    return { ...row, products: updatedProducts }
                  }
                  return row
                })

                return {
                  ...oldData,
                  document_detail_rows: updatedRows,
                }
              }
            )
          }
        }

        return { previousData }
      },
      onSuccess: () => {
        // Invalidate product mapping overview
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/mapping/product/overview'],
        })
        // Invalidate match detail queries to refresh with server data
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })
        // Invalidate metadata queries to refresh mapping options
        queryClient.invalidateQueries({
          queryKey: ['metadata'],
        })
      },
      onError: (error, request, context: OptimisticUpdateContext | undefined) => {
        // Rollback optimistic update on error
        if (context?.previousData && context.previousData.length > 0) {
          context.previousData.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey as string[], data)
          })
        }
      },
    })
  }

  const useToggleInvoiceHold = () => {
    return $api.useMutation('post', '/supplier_rec/supplier/set_hold', {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/config/overview'],
        })
      },
    })
  }

  const useTerminalMappingsOverview = () => {
    return $api.useQuery('post', '/supplier_rec/mapping/terminal/overview')
  }

  const useProductMappingsOverview = (params?: InferRequest<'/supplier_rec/mapping/product/overview'>) => {
    return $api.useQuery('post', '/supplier_rec/mapping/product/overview', {
      body: params,
    })
  }

  const useBulkUnmatch = $api.useMutation('post', '/supplier_rec/match/unlink_many', {
    onMutate: async (request) => {
      await cacheUtils.cancelMatchQueries()
      const previousData = cacheUtils.getPreviousQueryData()
      return { previousData, request }
    },
    onSuccess: (response) => {
      const cacheUpdate = cacheUtils.processApiResponse(response, 'link')
      cacheUtils.updateMatchOverviewCache(cacheUpdate)
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        cacheUtils.rollbackQueryData(context.previousData)
      }
    },
  })

  return {
    // Configuration hooks
    useConfigOverview,
    useUpsertConfig,
    useUpdateDefaultConfig,

    // Mapping hooks
    useAddSupplierMapping,
    useUpsertTerminalMapping,
    useUpsertProductMapping,
    useToggleInvoiceHold,
    useTerminalMappingsOverview,
    useProductMappingsOverview,

    // Match hooks from specialized hook
    ...matchHooks,

    // Match action hooks from specialized hook
    ...matchActionHooks,

    // Invoice action hooks from specialized hook
    ...invoiceActionHooks,

    // Match detail hooks from specialized hook
    ...matchDetailHooks,

    // Recent activity hooks from specialized hook
    ...recentActivityHooks,

    // Dashboard trends hook
    useSupplierRecDashboardTrends: trendsHook,

    // Tax action hooks from specialized hook
    ...taxActionHooks,

    // Alias for backward compatibility
    useRecalculateTaxesMutation: taxActionHooks.useRecalculateMatch,
    useBulkUnmatch,
  }
}
